<template>
  <div class="advisers-wrapper">
    <div class="advisers-container">
      <div class="advisers-section">
        <h3>Club Advisers</h3>

        <!-- Filter Section -->
        <div class="filter-section">
          <div class="filter-row">
            <el-form :inline="true" size="small">
              <el-form-item label="Filter by Advisory Class">
                <el-select
                  v-model="filterAdvisoryClass"
                  placeholder="Select class year"
                  style="width: 150px">
                  <el-option
                    label="All"
                    value="">
                  </el-option>
                  <el-option
                    v-for="year in classYearOptions"
                    :key="year"
                    :label="year"
                    :value="year">
                  </el-option>
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-checkbox v-model="showCurrentOnly">Show current advisers only</el-checkbox>
              </el-form-item>
            </el-form>

            <el-alert
              title="Note: By default, only current advisers (whose tenure includes today) are shown."
              type="info"
              :closable="false"
              show-icon
              style="flex: 1; margin-left: 16px;">
            </el-alert>
          </div>
        </div>

        <el-table :data="filteredAdvisers" style="width: 100%; margin-top: 20px;">
          <el-table-column label="Name" min-width="180">
            <template slot-scope="scope">
              {{ getAdviserFullName(scope.row) }}
            </template>
          </el-table-column>

          <el-table-column label="Email" min-width="200">
            <template slot-scope="scope">
              {{ scope.row.contact_email || 'N/A' }}
            </template>
          </el-table-column>

          <el-table-column label="Tenure Start" min-width="120">
            <template slot-scope="scope">
              {{ scope.row.tenure_start || '-' }}
            </template>
          </el-table-column>

          <el-table-column label="Tenure End" min-width="120">
            <template slot-scope="scope">
              {{ scope.row.tenure_end || '-' }}
            </template>
          </el-table-column>

          <el-table-column label="Advisory Class Year" min-width="140">
            <template slot-scope="scope">
              {{ scope.row.advisory_class || '-' }}
            </template>
          </el-table-column>

          <el-table-column label="Status" min-width="100">
            <template slot-scope="scope">
              <el-tag :type="isCurrentAdviser(scope.row) ? 'success' : 'info'" size="small">
                {{ isCurrentAdviser(scope.row) ? 'Current' : 'Past/Future' }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="Actions" min-width="120">
            <template slot-scope="scope">
              <el-button
                v-if="isCurrentUser(scope.row)"
                :disabled="!canViewCertificate(scope.row)"
                :type="canViewCertificate(scope.row) ? 'primary' : 'info'"
                size="small"
                @click="viewCertificate(scope.row)">
                Certificate
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div v-if="!filteredAdvisers || filteredAdvisers.length === 0" class="no-advisers">
          <p>No advisers found matching the current filters.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAllClubAdvisors } from '@/api/eclub'
import { getUserId, getUserType, getUserEmail } from '@/utils/store'

export default {
  name: 'Advisers',
  props: {
    clubInfo: {
      type: Object,
      default: () => ({}),
    },
    club_id: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      allAdvisers: [],
      filterAdvisoryClass: '',
      showCurrentOnly: true,
      classYearOptions: this.generateClassYearOptions(),
      currentUserId: getUserId(),
      currentUserType: getUserType(),
      currentUserEmail: getUserEmail()
    }
  },

  computed: {
    advisers() {
      return this.allAdvisers.length > 0 ? this.allAdvisers : (this.clubInfo.advisors || [])
    },
    filteredAdvisers() {
      let result = this.advisers

      // Filter by advisory class
      if (this.filterAdvisoryClass) {
        result = result.filter(a => a.advisory_class === this.filterAdvisoryClass)
      }

      // Filter by current tenure
      if (this.showCurrentOnly) {
        result = result.filter(a => this.isCurrentAdviser(a))
      }

      return result
    }
  },

  watch: {
    showCurrentOnly(newVal) {
      // When toggling to show all, fetch all advisers
      if (!newVal && this.allAdvisers.length === 0) {
        this.fetchAllAdvisers()
      }
    }
  },

  mounted() {
    // Initially load only current advisers from clubInfo
    // When user unchecks "current only", we fetch all advisers
  },

  methods: {
    generateClassYearOptions() {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let i = 3; i >= -3; i--) {
        years.push(String(currentYear + i))
      }
      return years
    },
    getAdviserFullName(adviser) {
      if (!adviser) return 'N/A'
      const firstName = adviser.contact_givenName || ''
      const lastName = adviser.contact_lastName || ''
      return `${firstName} ${lastName}`.trim() || 'N/A'
    },
    isCurrentAdviser(adviser) {
      const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD

      const tenureStart = adviser.tenure_start
      const tenureEnd = adviser.tenure_end

      // If no tenure dates set, consider as current (backward compatibility)
      if (!tenureStart && !tenureEnd) {
        return true
      }

      // Check if tenure_start is after today
      if (tenureStart && tenureStart > today) {
        return false
      }

      // Check if tenure_end is before today
      if (tenureEnd && tenureEnd < today) {
        return false
      }

      return true
    },
    async fetchAllAdvisers() {
      try {
        const res = await getAllClubAdvisors(this.club_id)
        if (res.data && res.data.data) {
          this.allAdvisers = res.data.data
        }
      } catch (err) {
        console.error('Error fetching all advisers:', err)
      }
    },
    canViewCertificate(adviser) {
      if (!adviser.tenure_end) return false
      const today = new Date().toISOString().split('T')[0]
      return today > adviser.tenure_end
    },
    isCurrentUser(adviser) {
      // Check if the logged-in user is an advisor and matches this adviser row
      if (this.currentUserType !== 'advisor') {
        return false
      }
      // Match by contact_id or email
      if (adviser.contact_id && adviser.contact_id === this.currentUserId) {
        return true
      }
      if (adviser.contact_email && adviser.contact_email === this.currentUserEmail) {
        return true
      }
      return false
    },
    viewCertificate(adviser) {
      const userName = this.getAdviserFullName(adviser)
      const schoolName = this.clubInfo.account_nameEN || this.clubInfo.account_name || ''

      const route = this.$router.resolve({
        path: '/certificate',
        query: {
          type: 'advisory',
          club_name: this.clubInfo.name,
          school_name: schoolName,
          user_name: userName,
          user_email: adviser.contact_email || '',
          tenure_start: adviser.tenure_start || '',
          tenure_end: adviser.tenure_end || ''
        }
      })
      window.open(route.href, '_blank')
    }
  }
}
</script>

<style scoped lang="scss">
.advisers-wrapper {
  min-height: calc(100vh - 64px);
  padding: 20px;
}

.advisers-container {
  max-width: 1200px;
  margin: 0 auto;
}

.advisers-section {
  background: white;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);

  h3 {
    margin-top: 0;
    margin-bottom: 24px;
    font-size: 18px;
    font-weight: 600;
  }
}

.filter-section {
  margin-bottom: 16px;

  .filter-row {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 16px;

    .el-form {
      margin-bottom: 0;
    }

    .el-form-item {
      margin-bottom: 0;
    }
  }
}

.no-advisers {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
  font-style: italic;

  p {
    margin: 0;
    font-size: 16px;
  }
}
</style>
